import { useCallback, useEffect, useRef, useState } from 'react';
import type { JSONContent } from '@tiptap/core';
import { authHeaders } from './useAuth';

export type SaveStatus = 'idle' | 'unsaved' | 'saving' | 'saved' | 'error';

const DEBOUNCE_MS = 5_000;
const SAVED_RESET_MS = 3_000;
const LS_KEY = (slug: string) => `reporter_draft_${slug}`;

export interface AutoSaveContent {
  title: string;
  content_json: JSONContent | null;
  content_html: string | null;
}

export interface DraftEntry extends AutoSaveContent {
  updated_at: string;
}

export function useAutoSave(slug: string) {
  const [status, setStatus] = useState<SaveStatus>('idle');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const savedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const save = useCallback(
    async (content: AutoSaveContent) => {
      abortRef.current?.abort();
      abortRef.current = new AbortController();

      setStatus('saving');
      try {
        const res = await fetch(`/api/v1/pages/${slug}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', ...authHeaders() },
          body: JSON.stringify(content),
          signal: abortRef.current.signal,
        });
        if (res.status === 401) {
          localStorage.removeItem('reporter.token');
          window.location.href = '/login';
          return;
        }
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        localStorage.removeItem(LS_KEY(slug));
        setStatus('saved');
        if (savedTimerRef.current) clearTimeout(savedTimerRef.current);
        savedTimerRef.current = setTimeout(() => setStatus('idle'), SAVED_RESET_MS);
      } catch (err: unknown) {
        if (err instanceof DOMException && err.name === 'AbortError') return;
        const draft: DraftEntry = { ...content, updated_at: new Date().toISOString() };
        localStorage.setItem(LS_KEY(slug), JSON.stringify(draft));
        setStatus('error');
      }
    },
    [slug]
  );

  const scheduleAutoSave = useCallback(
    (content: AutoSaveContent) => {
      setStatus('unsaved');
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => save(content), DEBOUNCE_MS);
    },
    [save]
  );

  const loadDraft = useCallback((): DraftEntry | null => {
    const raw = localStorage.getItem(LS_KEY(slug));
    if (!raw) return null;
    try {
      return JSON.parse(raw) as DraftEntry;
    } catch {
      return null;
    }
  }, [slug]);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (savedTimerRef.current) clearTimeout(savedTimerRef.current);
      abortRef.current?.abort();
    };
  }, []);

  return { status, scheduleAutoSave, loadDraft };
}
